import type { ApiQuestion } from '../../../internalTypes';
import type { QuestionBeforeLogics } from './shared/questionMapperTypes';
import { QUESTION_TYPE } from '../../../constants/questionTypes';
import { mapMcqQuestion } from './mcq/mapMcqQuestion';
import { mapNpsQuestion } from './nps/mapNpsQuestion';
import { mapTextInputQuestion } from './textInput/mapTextInputQuestion';
import { mapCfmMatrixQuestion } from './cfmMatrix/mapCfmMatrixQuestion';
import { mapCsatQuestion } from './csat/mapCsatQuestion';
import { mapRatingMatrixQuestion } from './ratingMatrix/mapRatingMatrixQuestion';
import { mapSliderMatrixQuestion } from './sliderMatrix/mapSliderMatrixQuestion';
import { mapTextAndMediaQuestion } from './textAndMedia/mapTextAndMediaQuestion';
import { mapFileUploadQuestion } from './fileUpload/mapFileUploadQuestion';
import { mapHeatmapQuestion } from './heatmap/mapHeatmapQuestion';
import { mapRankOrderQuestion } from './rankOrder/mapRankOrderQuestion';
import type { ResolvedQuestionShell } from './shared/resolveQuestionShell';

type QuestionMapperContext = {
  raw: ApiQuestion;
  shell: ResolvedQuestionShell;
  configs: { key: string; value?: string[] }[];
  language?: string;
};

type QuestionMapperFn = (ctx: QuestionMapperContext) => QuestionBeforeLogics;

const mapMcq: QuestionMapperFn = ({ raw, shell, language }) =>
  mapMcqQuestion(raw, shell, language);

const mapNps: QuestionMapperFn = ({ raw, shell, configs, language }) =>
  mapNpsQuestion(raw, shell, configs, language);

const mapTextInput: QuestionMapperFn = ({ raw, shell, configs, language }) =>
  mapTextInputQuestion(raw, shell, configs, language);

const mapCfmMatrix: QuestionMapperFn = ({ raw, shell, language }) =>
  mapCfmMatrixQuestion(raw, shell, language);

const mapCsatMatrix: QuestionMapperFn = ({ raw, shell, language }) =>
  mapCsatQuestion(raw, shell, language);

const mapRatingMatrix: QuestionMapperFn = ({ raw, shell, language }) =>
  mapRatingMatrixQuestion(raw, shell, language);

const mapSliderMatrix: QuestionMapperFn = ({ raw, shell, language }) =>
  mapSliderMatrixQuestion(raw, shell, language);

const mapTextAndMedia: QuestionMapperFn = ({ raw, shell }) =>
  mapTextAndMediaQuestion(raw, shell);

const mapFileUpload: QuestionMapperFn = ({ raw, shell, language }) =>
  mapFileUploadQuestion(raw, shell, language);

const mapHeatmap: QuestionMapperFn = ({ raw, shell }) => mapHeatmapQuestion(raw, shell);

const mapRankOrder: QuestionMapperFn = ({ raw, shell, configs, language }) =>
  mapRankOrderQuestion(raw, shell, configs, language);

export const QUESTION_MAPPERS: Record<string, QuestionMapperFn> = {
  [QUESTION_TYPE.MCQ]: mapMcq,
  [QUESTION_TYPE.NPS_SCALE]: mapNps,
  SCALE: mapNps,
  [QUESTION_TYPE.TEXTFIELD]: mapTextInput,
  [QUESTION_TYPE.CFM_MATRIX]: mapCfmMatrix,
  [QUESTION_TYPE.CSAT_MATRIX]: mapCsatMatrix,
  [QUESTION_TYPE.RATING_MATRIX]: mapRatingMatrix,
  [QUESTION_TYPE.SLIDER_MATRIX]: mapSliderMatrix,
  [QUESTION_TYPE.TEXT_AND_MEDIA]: mapTextAndMedia,
  [QUESTION_TYPE.FILE_UPLOAD]: mapFileUpload,
  [QUESTION_TYPE.HEATMAP]: mapHeatmap,
  [QUESTION_TYPE.RANK_ORDER]: mapRankOrder,
};

export function resolveQuestionMapper(answerType: string | undefined): QuestionMapperFn | undefined {
  if (!answerType) return undefined;
  return QUESTION_MAPPERS[answerType];
}
